import React from 'react';
import { Icon } from './Icons';
import { F, EASE } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import { usePress } from './Press';

const RED = '#E4544A';
// Calming-Pass: ruhiges, tiefes Mattrot für Primäraktionen (kein Neon, kein Glow).
const RED_CALM_TOP = '#C2453F', RED_CALM = '#B33E3A';

export default function RedButton({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  fullWidth = false,
  disabled = false,
  style = {},
  type,
  title,
}) {
  const { isDark, isMobile } = useTheme();
  const { pressTransform, pressHandlers } = usePress({ disabled });
  const H = size === 'sm' ? 36 : size === 'lg' ? 52 : 44;
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 14 : 13;
  const padX = size === 'sm' ? 12 : size === 'lg' ? 18 : 15;
  const iconSize = size === 'sm' ? 14 : size === 'lg' ? 16 : 15;

  let bg, color, border, shadow;
  if (variant === 'primary') {
    // Ruhiges tiefes Mattrot — kein Neon-Highlight, KEIN roter Glow.
    bg = `linear-gradient(180deg, ${RED_CALM_TOP} 0%, ${RED_CALM} 100%)`;
    color = '#fff';
    border = '1px solid rgba(0,0,0,0.06)';
    shadow = 'inset 0 1px 0 rgba(255,255,255,0.14), 0 1px 2px rgba(0,0,0,0.16)';
  } else if (variant === 'outline') {
    // Sekundäraktion — nur dünne Linie, neutral (kein Rot).
    bg = 'transparent';
    color = isDark ? '#F4F4F8' : '#161618';
    border = isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(12,12,15,0.14)';
    shadow = 'none';
  } else if (variant === 'text') {
    // Tertiäraktion — reiner Text-Link, kein Container.
    bg = 'transparent';
    color = isDark ? '#9090A0' : '#5F5E5A';
    border = 'none';
    shadow = 'none';
  } else if (variant === 'secondary') {
    bg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(12,12,15,0.06)';
    color = isDark ? '#F4F4F8' : '#161618';
    border = isDark ? 'none' : '1px solid rgba(12,12,15,0.08)';
    shadow = isDark ? 'inset 0 1px 0 rgba(255,255,255,0.06)' : 'inset 0 1px 0 rgba(255,255,255,0.6)';
  } else if (variant === 'ghost') {
    bg = 'transparent';
    color = RED;
    border = 'none';
    shadow = isDark ? 'inset 0 0 0 1px rgba(255,255,255,0.06)' : 'inset 0 0 0 1px rgba(12,12,15,0.10)';
  } else if (variant === 'silver') {
    bg = 'linear-gradient(155deg, #EDEEF5 0%, #D4D5DE 40%, #8E8F98 100%)';
    color = '#161618';
    border = '1px solid rgba(255,255,255,0.18)';
    shadow = isMobile
      ? '0 8px 22px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.15)'
      : '0 8px 22px rgba(212,213,222,0.40), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.15)';
  } else {
    // dark — invertiert sich im Light-Mode: weisser Hintergrund + dunkles Icon
    if (isDark) {
      bg = '#1A1A22';
      color = '#F4F4F8';
      border = '1px solid rgba(255,255,255,0.08)';
      shadow = 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 14px rgba(0,0,0,0.25)';
    } else {
      bg = '#FFFFFF';
      color = '#161618';
      border = '1px solid rgba(12,12,15,0.14)';
      shadow = 'inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 8px rgba(12,12,15,0.10)';
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type={type}
      title={title}
      {...pressHandlers}
      style={{
        position: 'relative',
        height: H,
        padding: `0 ${padX}px`,
        background: bg,
        color,
        border,
        boxShadow: shadow,
        borderRadius: 12,
        fontFamily: F.family,
        fontSize,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        width: fullWidth ? '100%' : undefined,
        WebkitTapHighlightColor: 'transparent',
        ...style,
        transform: pressTransform,
        transition: `transform 0.14s ${EASE}, box-shadow 0.18s ease`,
      }}
    >
      {icon && <Icon d={icon} size={iconSize} stroke={color} sw={2.4} />}
      {children && <span>{children}</span>}
      {iconRight && <Icon d={iconRight} size={iconSize} stroke={color} sw={2.4} />}
    </button>
  );
}

export function RedIconButton({
  icon,
  onClick,
  size = 44,
  shape = 'square',
  variant = 'dark',
  title,
  badge,
  style = {},
}) {
  const { isDark, isMobile } = useTheme();
  const { pressTransform, pressHandlers } = usePress({ scale: 0.92 });
  const radius = shape === 'circle' ? size / 2 : 13;
  let bg, border, color, shadow;
  if (variant === 'primary') {
    bg = `linear-gradient(180deg, ${RED_CALM_TOP} 0%, ${RED_CALM} 100%)`;
    border = '1px solid rgba(0,0,0,0.06)';
    color = '#fff';
    shadow = 'inset 0 1px 0 rgba(255,255,255,0.14), 0 1px 2px rgba(0,0,0,0.16)';
  } else {
    // dark — invertiert sich im Light-Mode: weisser Hintergrund + dunkles Icon
    if (isDark) {
      bg = '#1A1A22';
      border = '1px solid rgba(255,255,255,0.08)';
      color = '#F4F4F8';
      shadow = 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 14px rgba(0,0,0,0.25)';
    } else {
      bg = '#FFFFFF';
      border = '1px solid rgba(12,12,15,0.14)';
      color = '#161618';
      shadow = 'inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 8px rgba(12,12,15,0.10)';
    }
  }
  return (
    <button
      onClick={onClick}
      title={title}
      {...pressHandlers}
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: radius,
        background: bg,
        color,
        border,
        boxShadow: shadow,
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        WebkitTapHighlightColor: 'transparent',
        ...style,
        transform: pressTransform,
        transition: `transform 0.14s ${EASE}`,
      }}
    >
      <Icon d={icon} size={size <= 36 ? 14 : size <= 44 ? 16 : 18} stroke={color} sw={2.4} />
      {badge && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 9,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: RED,
            boxShadow: isMobile ? 'none' : `0 0 8px ${RED}`,
          }}
        />
      )}
    </button>
  );
}
